import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Head from 'next/head'
import { Grid } from '@hermes/web-components'
import { useRouter } from 'next/router'
import SearchLayout from '../SearchLayout'
import SearchProfilesResults from '../SearchResults'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import PageMeta from '../../../ProfileMeta'
import useSearchMeta from '../../../../hooks/useSearchMeta'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { Insurer, Language } from '../../../../types'
import { QuickFactsAndQuestionsContent } from '../../../../types/quickFacts'
import { SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import { AppDataContext } from '../../../../providers/AppData'
import Breadcrumbs from '../../../Breadcrumbs'
import { capitalise } from '../../../../utils/strings'
import { FILTERS_TO_EXCLUDE_FROM_BREADCRUMBS } from '../../../../constants/search'
import { useWpaTenant } from '../../../../hooks/useWpaTenant'
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags'
import QuickFactsAndQuestions from '../../Alternative/QuickFactsAndQuestions'
import JsonLd from '../../../JsonLd'

const stage = process.env.NEXT_PUBLIC_STAGE

type SearchContentProps = {
  languages: Language[]
  insurers: Insurer[]
  quickFactsAndQuestions?: QuickFactsAndQuestionsContent
}

// TODO: refactor this page
// eslint-disable-next-line complexity
function SearchContent({ insurers, languages, quickFactsAndQuestions }: SearchContentProps) {
  const translate = useTranslateMessage()
  const searchProfilesPageProviderState = useContext(SearchProfilesPageContext)
  const {
    state: { specialty, filters, practices, specialists },
    searchKeywordTitle
  } = searchProfilesPageProviderState
  const { language, locale } = useContext(AppDataContext)
  const [breadcrumbs, setBreadcrumbs] = useState(filters?.breadcrumb || [])
  const router = useRouter()
  const extMode = router.asPath.includes('ext-')
  const { isWPA } = useWpaTenant()
  const { alternativeSearchEnabled } = useFeatureFlags()
  const locationUrl = router.asPath.split('/')?.[3] || ''
  const isNoindex = locationUrl?.includes('ext-') || (!practices.length && !specialists.length)
  let location = filters.location
  if (filters.parentLocation) {
    location += `, ${filters.parentLocation}`
  }

  const pageMeta = useSearchMeta(location, searchKeywordTitle, filters.page, isNoindex, filters.searchType)

  const breadcrumbsJsonLd = useMemo(() => {
    const suffix = stage === 'production' ? 'www.' : `${stage}.`
    const itemListElement = filters?.breadcrumb?.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'WebPage',
        id: `https://${suffix}doctify.com${breadcrumb.url}`,
        name: breadcrumb.label
      }
    }))

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement
    })
  }, [filters?.breadcrumb])

  const getUrlWithParams = useCallback(
    (url: string) => {
      if (!router.asPath.includes('#')) {
        return url
      }
      const allParams = router.asPath
        .split('#')[1]
        .split('&')
        .filter((filter) => {
          const key = filter.split('=')[0]
          return !FILTERS_TO_EXCLUDE_FROM_BREADCRUMBS.includes(key)
        })

      return allParams.length > 1 ? `${url}&${allParams.slice(1).join('&')}` : url
    },
    [router.asPath]
  )

  const getNamedString = useCallback(
    (name: string) => {
      return translate('search.breadcrumb_location', {
        name,
        location: capitalise(decodeURIComponent(decodeURI(filters.location || locale)))
      })
    },
    [filters.location, locale, translate]
  )

  const getBreadcrumbsLabel = useCallback(() => {
    const defaultPracticeLabel = translate('search.breadcrumb_location_practices', {
      name: specialty?.name?.[language].toUpperCase(),
      location: decodeURIComponent(decodeURI(filters.location)).toUpperCase()
    })
    const defaultSpecialistLabel = translate('search.breadcrumb_location', {
      name: specialty.id === 1 ? translate('search.all_doctors') : `${searchKeywordTitle || ''}`?.toUpperCase(),
      location: capitalise(decodeURIComponent(decodeURI(filters.location || locale)))
    })

    switch (filters.searchType) {
      case SearchTypes.specialists:
        return defaultSpecialistLabel
      case SearchTypes.practices:
        return defaultPracticeLabel
      case SearchTypes.dentist:
        return defaultPracticeLabel
      case SearchTypes.hospital:
        return getNamedString(translate('hospitals'))
      case SearchTypes.careHome:
        return getNamedString(translate('care-home'))
      case SearchTypes.pharmacy:
        return getNamedString(translate('pharmacies'))
      case SearchTypes.healthAndSocialCare:
        return getNamedString(translate('home-care-spec'))
      default:
        return defaultPracticeLabel
    }
  }, [
    filters.searchType,
    filters.location,
    getNamedString,
    language,
    locale,
    searchKeywordTitle,
    specialty.id,
    specialty?.name,
    translate
  ])

  const getBreadcrumbsItems = useCallback(() => {
    const isAlternativeSearchTypeSelected = filters.searchType && alternativeSearchEnabled && !isWPA
    const defaultLabelName =
      specialty.id === 1 ? translate('search.all_doctors') : `${searchKeywordTitle || ''}`?.toUpperCase()
    const defaultLabel =
      filters.searchType === SearchTypes.specialists || filters.searchType === SearchTypes.healthAndSocialCare
        ? getNamedString(defaultLabelName)
        : translate('search.breadcrumb_location_practices', {
            name: specialty?.name?.[language].toUpperCase(),
            location: decodeURIComponent(decodeURI(filters.location)).toUpperCase()
          })
    const alternativeLabel = getBreadcrumbsLabel()

    return [
      ...breadcrumbs.slice(0, -1),
      {
        label: isAlternativeSearchTypeSelected ? alternativeLabel : defaultLabel,
        url: filters.breadcrumb?.[filters.breadcrumb.length - 1].url || ''
      }
    ]
  }, [
    filters.searchType,
    alternativeSearchEnabled,
    isWPA,
    breadcrumbs,
    filters.breadcrumb,
    filters.location,
    getBreadcrumbsLabel,
    getNamedString,
    language,
    searchKeywordTitle,
    specialty.id,
    specialty?.name,
    translate
  ])

  useEffect(() => {
    setBreadcrumbs(
      (prevBreadcrumbs) => prevBreadcrumbs?.map((item) => ({ ...item, url: getUrlWithParams(item.url) })) || []
    )
  }, [getUrlWithParams, router.asPath])

  const noFilters = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.hash.replace('#', ''))
      return params.size === 0 || (params.size === 1 && params.get('location'))
    }

    // can't get params on server side due to hash - always true
    return true
    // subscribe to search state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchProfilesPageProviderState])

  return (
    <>
      <PageMeta {...pageMeta} />
      <SearchLayout insurers={insurers} languages={languages}>
        <SearchProfilesResults />
      </SearchLayout>
      {!isWPA && quickFactsAndQuestions && noFilters ? (
        <QuickFactsAndQuestions data={quickFactsAndQuestions} containerStyle={{ paddingTop: '30px' }} />
      ) : null}
      {Boolean(filters.breadcrumb?.length) && !extMode && (filters.breadcrumb?.length || 0) > 1 && !isWPA && (
        <Grid sx={{ py: 5, px: '15%', backgroundColor: 'background.light' }}>
          <Head>
            <JsonLd schema={breadcrumbsJsonLd} />
          </Head>
          <Breadcrumbs items={getBreadcrumbsItems()} />
        </Grid>
      )}
    </>
  )
}

export default SearchContent
